import {
  Table,
  TableContainer,
  TableBody,
  TableHead,
  TableRow,
  Stack,
  MenuItem,
  TableCell,
  IconButton,
  Button,
  Menu,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import {
  AllPartneringFamiliesPermissionContext,
  AllVolunteerFamiliesPermissionContext,
  AssignedFunctionsInReferralCoAssigneeFamiliesPermissionContext as AssignedFunctionsInV1CaseCoAssigneeFamiliesPermissionContext,
  AssignedFunctionsInReferralPartneringFamilyPermissionContext as AssignedFunctionsInV1CasePartneringFamilyPermissionContext,
  AssignedVolunteerInV1CasePermissionContext,
  AssignedVolunteerInV1ReferralPermissionContext,
  CommunityCoMemberFamiliesPermissionContext,
  CommunityMemberPermissionContext,
  ContextualPermissionSet,
  GlobalPermissionContext,
  IContextualPermissionSet,
  OwnFamilyPermissionContext,
  OwnReferralAssigneeFamiliesPermissionContext as OwnV1CaseAssigneeFamiliesPermissionContext,
  PermissionContext,
  RoleDefinition,
  Permission,
  CommunityCoMemberFamiliesAssignedFunctionsInReferralPartneringFamilyPermissionContext as CommunityCoMemberFamiliesAssignedFunctionsInV1CasePartneringFamilyPermissionContext,
  CommunityCoMemberFamiliesAssignedFunctionsInReferralCoAssignedFamiliesPermissionContext as CommunityCoMemberFamiliesAssignedFunctionsInV1CaseCoAssignedFamiliesPermissionContext,
} from '../../GeneratedClient';
import { organizationConfigurationEdited } from '../../Model/ConfigurationModel';
import { useGlobalPermissions } from '../../Model/SessionModel';
import { Add as AddIcon } from '@mui/icons-material';
import { useBackdrop } from '../../Hooks/useBackdrop';
import { useSetAtom } from 'jotai';
import { ContextualPermissionSetRow } from './ContextualPermissionSetRow';
import { api } from '../../Api/Api';
import { useRequiredSelectedLocationContext } from '../../Model/Data';
import { Box } from '@mui/system';
import { isRoleEditable } from './isRoleEditable';
import { ContextualPermissionSetRowAutocomplete } from './ContextualPermissionSetRowWithAutocomplete';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Breadcrumbs } from '../../Generic/Breadcrumbs';
import {
  DESKTOP_BOTTOM_SAFE_AREA,
  MOBILE_BOTTOM_SAFE_AREA,
  SHELL_APP_BAR_HEIGHT,
} from '../../Shell/shellLayoutConstants';
import { PERMISSIONS_AUTOCOMPLETE_FEATURE_FLAG } from '../../featureFlags';

export function RoleEdit({
  roleDefinition,
}: {
  roleDefinition: RoleDefinition;
}) {
  const permissions = useGlobalPermissions();

  const storeEdits = useSetAtom(organizationConfigurationEdited);

  const { organizationId } = useRequiredSelectedLocationContext();

  const [workingRole, setWorkingRole] =
    useState<RoleDefinition>(roleDefinition);

  const [dirty, setDirty] = useState(false);

  const isEditable = Boolean(
    workingRole &&
      permissions(Permission.AddEditRoles) &&
      isRoleEditable(workingRole)
  );

  function cancel() {
    setWorkingRole(roleDefinition);
    setDirty(false);
  }

  const withBackdrop = useBackdrop();

  function save() {
    withBackdrop(async () => {
      const newConfig = await api.configuration.putRoleDefinition(
        organizationId,
        workingRole.roleName!,
        workingRole
      );
      storeEdits(newConfig);
      setDirty(false);
    });
  }

  function deletePermissionSetAtIndex(i: number) {
    const newPermissionSets = workingRole.permissionSets!.filter(
      (_, j) => j !== i
    );
    const newWorkingRole = {
      roleName: workingRole.roleName,
      permissionSets: newPermissionSets,
    } as RoleDefinition;
    setWorkingRole(newWorkingRole);
    setDirty(true);
  }

  function updatePermissionSetAtIndex(
    i: number,
    newValue: IContextualPermissionSet
  ) {
    const newPermissionSets = workingRole.permissionSets!.map((oldValue, j) =>
      j === i ? newValue : oldValue
    );
    const newWorkingRole = {
      roleName: workingRole.roleName,
      permissionSets: newPermissionSets,
    } as RoleDefinition;
    setWorkingRole(newWorkingRole);
    setDirty(true);
  }

  const [addPermissionSetMenuAnchorEl, setAddPermissionSetMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  function addPermissionSet<T extends PermissionContext>(factory: () => T) {
    const newContext = factory();
    const newSet = new ContextualPermissionSet({
      context: newContext,
      permissions: [],
    });
    const newWorkingRole = {
      roleName: workingRole.roleName,
      permissionSets: workingRole.permissionSets!.concat(newSet),
    } as RoleDefinition;
    setWorkingRole(newWorkingRole);
    setDirty(true);
    setAddPermissionSetMenuAnchorEl(null);
  }

  const shouldUseAutocomplete = useFeatureFlagEnabled(
    PERMISSIONS_AUTOCOMPLETE_FEATURE_FLAG
  );

  return (
    <Stack
      className="ph-unmask"
      sx={{
        paddingTop: { xs: 1, sm: 2 },
        minWidth: 0,
        minHeight: 0,
        height: {
          xs: `calc(100dvh - ${SHELL_APP_BAR_HEIGHT.xs} - ${MOBILE_BOTTOM_SAFE_AREA}px)`,
          sm: `calc(100dvh - ${SHELL_APP_BAR_HEIGHT.sm} - ${MOBILE_BOTTOM_SAFE_AREA}px)`,
          md: `calc(100dvh - ${SHELL_APP_BAR_HEIGHT.md} - ${DESKTOP_BOTTOM_SAFE_AREA}px)`,
        },
      }}
      spacing={0}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Breadcrumbs
          items={[
            {
              label: 'Settings',
              to: '../..',
              relative: 'path',
            },
            {
              label: 'Roles',
              to: '..',
              relative: 'path',
            },
          ]}
          currentPageLabel={roleDefinition.roleName || ''}
        />

        <Typography sx={{ my: { xs: 1, sm: 2 } }} variant="h2">
          Editing {roleDefinition.roleName} role
        </Typography>
      </Box>

      <TableContainer
        sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto' }}
      >
        <Table sx={{ minWidth: '700px' }} stickyHeader size="small">
          <colgroup>
            <col />
            <col style={{ width: '50%' }} />
            <col style={{ width: '50%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Context</TableCell>
              <TableCell>Permissions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workingRole?.permissionSets?.map((permissionSet, i) =>
              shouldUseAutocomplete ? (
                <ContextualPermissionSetRowAutocomplete
                  key={`${workingRole.permissionSets?.length}-${i}`}
                  editable={isEditable}
                  permissionSet={permissionSet}
                  onDelete={() => deletePermissionSetAtIndex(i)}
                  onUpdate={(newValue: IContextualPermissionSet) =>
                    updatePermissionSetAtIndex(i, newValue)
                  }
                />
              ) : (
                <ContextualPermissionSetRow
                  key={`${workingRole.permissionSets?.length}-${i}`}
                  editable={isEditable}
                  permissionSet={permissionSet}
                  onDelete={() => deletePermissionSetAtIndex(i)}
                  onUpdate={(newValue: IContextualPermissionSet) =>
                    updatePermissionSetAtIndex(i, newValue)
                  }
                />
              )
            )}
            {isEditable && (
              <TableRow>
                <TableCell>
                  <IconButton
                    onClick={(event) =>
                      setAddPermissionSetMenuAnchorEl(event.currentTarget)
                    }
                  >
                    <AddIcon />
                  </IconButton>
                </TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        open={Boolean(addPermissionSetMenuAnchorEl)}
        anchorEl={addPermissionSetMenuAnchorEl}
        onClose={() => setAddPermissionSetMenuAnchorEl(null)}
      >
        <MenuItem
          dense
          onClick={() => addPermissionSet(() => new GlobalPermissionContext())}
        >
          Global
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(() => new OwnFamilyPermissionContext())
          }
        >
          Own Family
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(() => new AllVolunteerFamiliesPermissionContext())
          }
        >
          All Volunteer Families
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(() => new AllPartneringFamiliesPermissionContext())
          }
        >
          All Partnering Families
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(
              () =>
                new AssignedFunctionsInV1CasePartneringFamilyPermissionContext()
            )
          }
        >
          Assigned Functions in Case - Partnering Family
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(
              () =>
                new AssignedFunctionsInV1CaseCoAssigneeFamiliesPermissionContext()
            )
          }
        >
          Assigned Functions in Case - Co-Assigned Families
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(
              () => new OwnV1CaseAssigneeFamiliesPermissionContext()
            )
          }
        >
          Own Case - Assigned Families
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(
              () => new AssignedVolunteerInV1CasePermissionContext()
            )
          }
        >
          Assigned Volunteer in Case
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(
              () => new AssignedVolunteerInV1ReferralPermissionContext()
            )
          }
        >
          Assigned Volunteer in Referral
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(() => new CommunityMemberPermissionContext())
          }
        >
          Organization Member - Organization
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(
              () => new CommunityCoMemberFamiliesPermissionContext()
            )
          }
        >
          Organization Member - Co-Member Families
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(
              () =>
                new CommunityCoMemberFamiliesAssignedFunctionsInV1CasePartneringFamilyPermissionContext()
            )
          }
        >
          Organization Member - Co-Member Families - Assigned Functions in Case
          - Partnering Family
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            addPermissionSet(
              () =>
                new CommunityCoMemberFamiliesAssignedFunctionsInV1CaseCoAssignedFamiliesPermissionContext()
            )
          }
        >
          Organization Member - Co-Member Families - Assigned Functions in Case
          - Co-Assigned Families
        </MenuItem>
      </Menu>

      <Box
        sx={{
          pt: { xs: 1, sm: 2 },
          flexShrink: 0,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {dirty && (
            <Typography sx={{ fontStyle: 'italic', mr: 2 }}>
              There are pending changes to be saved
            </Typography>
          )}

          {isEditable && (
            <Button
              color="secondary"
              variant="contained"
              sx={{ marginRight: 2 }}
              disabled={!dirty}
              onClick={cancel}
            >
              Cancel
            </Button>
          )}
          {isEditable && (
            <Button
              color="primary"
              variant="contained"
              disabled={!dirty}
              onClick={save}
            >
              Save
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
